import { SessionState } from '@heygen/liveavatar-web-sdk';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getHeyGenAvatarId } from '../../config/avatarMapping';
import { type ChatResponse, sendChatMessage } from '../../services/chatService';
import {
  endSession,
  endSessionBeacon,
  leaveQueueBeacon,
  sendSessionPing,
} from '../../services/heygenQueueService';
import { getHeyGenSessionTokenWithRetry } from '../../services/heygenService';
import logger from '../../utils/logger';
import { AvatarControls } from './components/AvatarControls';
import { AvatarVideo } from './components/AvatarVideo';
import { QueueOverlay } from './components/QueueOverlay';
import type { SessionCloseReason } from './context/StreamingAvatarContext';
import {
  MessageSender,
  StreamingAvatarProvider,
  useStreamingAvatarContext,
} from './context/StreamingAvatarContext';
import { useHeyGenQueue } from './hooks/useHeyGenQueue';
import { useStreamingAvatarSession } from './hooks/useStreamingAvatarSession';
import { useWebSpeechSTT } from './hooks/useWebSpeechSTT';

import './HeyGenAvatar.css';

export interface HeyGenAvatarHandle {
  speak: (text: string) => Promise<void>;
}

// Props for the HeyGenAvatar component
interface HeyGenAvatarProps {
  companionId: string;
  companionImage?: string;
  companionName?: string;
  conversationId?: string | null;
  showMobileChatOverlay?: boolean;
  onToggleMobileChatOverlay?: () => void;
  showDesktopChat?: boolean;
  onToggleDesktopChat?: () => void;
  sttLanguage: string;
  onClose?: () => void;
  onCancelStart?: () => void;
  /** Called when the avatar starts connecting (loading screen visible) */
  onConnecting?: () => void;
  /** Called when the avatar session is fully connected and ready */
  onConnected?: () => void;
  onConversationCreated?: (conversationId: string) => void;
  onMessageSent?: (userMessage: string, avatarResponse: string) => void;
  onError?: (error: Error) => void;
  onCreditError?: (type: 'daily_limit' | 'no_credits') => void;
  /** Called when user wants to buy more credits from the warning overlay */
  onBuyCredits?: () => void;
  /** Called when the avatar chrome (controls/input) visibility changes */
  onChromeVisibilityChange?: (visible: boolean) => void;
}

// Max consecutive heartbeat failures before ending session (~3 minutes)
const MAX_HEARTBEAT_FAILURES = 3;

// UI-only idle timeout (60s idle → 30s warning → Close)
const IDLE_WARNING_TIME = 30000;
const IDLE_CLOSE_TIME = 30000;

// Inner component that uses the context
const HeyGenAvatarInner = forwardRef(
  (
    {
      companionId,
      companionImage,
      companionName,
      conversationId,
      showMobileChatOverlay = false,
      onToggleMobileChatOverlay,
      showDesktopChat = true,
      onToggleDesktopChat,
      sttLanguage,
      onClose,
      onCancelStart,
      onConnecting,
      onConnected,
      onConversationCreated,
      onMessageSent,
      onError,
      onCreditError,
      onBuyCredits,
      onChromeVisibilityChange,
    }: HeyGenAvatarProps,
    ref,
  ) => {
    const { t } = useTranslation();
    const mediaRef = useRef<HTMLVideoElement>(null);

    const { initAvatar, startAvatar, stopAvatar, sessionState, sessionCloseReason } =
      useStreamingAvatarSession();
    const {
      addMessage,
      setIsListening,
      avatarRef,
      setSessionCloseReason,
      isAvatarTalking,
      setIsAvatarTalking,
      isMicMuted,
      setIsMicMuted,
    } = useStreamingAvatarContext();

    // Warning state for session closure (not error - for idle timeout)
    const [warning, setWarning] = useState<string | null>(null);

    // Credit tracking via heartbeat
    const [creditsRemaining, setCreditsRemaining] = useState<number | null>(null);
    const [creditLimitWarning, setCreditLimitWarning] = useState(false);
    const heartbeatFailuresRef = useRef(0);
    // Track whether backend session was created (token obtained) for cleanup on cancel/tab close
    const sessionTokenObtainedRef = useRef(false);

    // ── Auto-hide chrome (controls + input bar) ──
    // After CHROME_HIDE_DELAY ms of no pointer/touch activity the chrome fades out.
    // Any activity reveals it again for another CHROME_HIDE_DELAY ms.
    const CHROME_HIDE_DELAY = 3500;
    const [chromeVisible, setChromeVisible] = useState(true);
    const chromeTimerRef = useRef<NodeJS.Timeout | null>(null);

    const revealChrome = useCallback(() => {
      setChromeVisible(true);
      if (chromeTimerRef.current) clearTimeout(chromeTimerRef.current);
      chromeTimerRef.current = setTimeout(() => {
        setChromeVisible(false);
      }, CHROME_HIDE_DELAY);
    }, []);

    // Notify parent of visibility transitions
    useEffect(() => {
      onChromeVisibilityChange?.(chromeVisible);
    }, [chromeVisible, onChromeVisibilityChange]);

    // Start the auto-hide timer once the session is connected, clean up on disconnect
    useEffect(() => {
      if (sessionState === SessionState.CONNECTED) {
        revealChrome();
      } else {
        // Always show chrome when not in an active session
        setChromeVisible(true);
        if (chromeTimerRef.current) {
          clearTimeout(chromeTimerRef.current);
          chromeTimerRef.current = null;
        }
      }
      return () => {
        if (chromeTimerRef.current) clearTimeout(chromeTimerRef.current);
      };
    }, [sessionState, revealChrome]);

    // Reveal chrome whenever avatar starts talking (natural moment to show controls)
    useEffect(() => {
      if (isAvatarTalking && sessionState === SessionState.CONNECTED) {
        revealChrome();
      }
    }, [isAvatarTalking, sessionState, revealChrome]);

    // Idle timer refs
    const idleWarningTimerRef = useRef<NodeJS.Timeout | null>(null);
    const idleDisconnectTimerRef = useRef<NodeJS.Timeout | null>(null);
    const lastActivityRef = useRef<number>(Date.now());

    // Ref to hold the latest handleUserMessage function (avoids stale closure)
    const handleUserMessageRef = useRef<(message: string) => Promise<void>>(undefined);

    // Ref to track when avatar.repeat() was called, to measure speak-start latency
    const speakCommandSentAtRef = useRef<number | null>(null);

    // Ref to track if credit warning was manually dismissed for the current session
    const creditWarningDismissedRef = useRef(false);

    // Ref to track latest isAvatarTalking (avoids stale closure in idle timer setTimeout)
    const isAvatarTalkingRef = useRef(false);
    const lastAvatarTalkedAtRef = useRef<number>(0);

    // Holds the text the avatar is currently speaking — used for echo detection
    // so we can keep the mic open (for user interruption) without accidentally
    // re-submitting the avatar's own words as a user question.
    const avatarSpeechTextRef = useRef('');

    const [isProcessingMessage, setIsProcessingMessage] = useState(false);
    const [isStarting, setIsStarting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [networkError, setNetworkError] = useState<string | null>(null);
    const networkErrorRetryRef = useRef<{ message: string } | null>(null);
    const [currentConversationId, setCurrentConversationId] = useState<string | null>(
      conversationId || null,
    );
    const [sttFeedback, setSttFeedback] = useState<string | null>(null);

    // ── Echo detection helper ──
    const isEchoTranscript = useCallback((text: string) => {
      const trimmed = text.trim();

      // Apply echo cancellation if avatar is actively talking OR stopped talking less than 3s ago
      // (because the browser's STT has 1-2s latency and emits final transcripts AFTER audio stops).
      const recentlyTalked =
        isAvatarTalkingRef.current || Date.now() - lastAvatarTalkedAtRef.current < 3000;

      if (!trimmed || !recentlyTalked || !avatarSpeechTextRef.current) return false;

      const normalize = (s: string) =>
        s
          .toLowerCase()
          .replace(/[^\p{L}\p{N}\s]/gu, '')
          .split(/\s+/)
          .filter(Boolean);
      const avatarWords = new Set(normalize(avatarSpeechTextRef.current));
      const transcriptWords = normalize(trimmed);
      if (transcriptWords.length === 0) return false;

      const overlapCount = transcriptWords.filter((w) => avatarWords.has(w)).length;
      const overlapRatio = overlapCount / transcriptWords.length;
      if (overlapRatio >= 0.3) return true;

      // Robust matching: STT often mis-transcribes echoes badly, so if it perfectly matches
      // at least ONE very long word (>= 5 chars) from the avatar, treat it as echo.
      const longMatches = transcriptWords.filter((w) => w.length >= 5 && avatarWords.has(w));
      if (longMatches.length >= 1) return true;

      return false;
    }, []);

    useEffect(() => {
      if (sttFeedback) {
        const timeout = setTimeout(() => {
          setSttFeedback(null);
        }, 3000);
        return () => clearTimeout(timeout);
      }
    }, [sttFeedback]);

    const {
      isListening: isVoiceListening,
      isSupported: isVoiceSupported,
      interimTranscript,
    } = useWebSpeechSTT({
      onTranscript: (transcript: string) => {
        const trimmedTranscript = transcript.trim();
        if (!trimmedTranscript) return;

        // ── Echo detection ──
        // The mic stays open during avatar speech so the user can interrupt.
        // But the browser's STT also picks up the avatar's speaker output.
        // We compare the incoming transcript against what the avatar is saying;
        // if >40% of words overlap it's almost certainly speaker echo → discard.
        if (isEchoTranscript(trimmedTranscript)) {
          logger.debug('HeyGenAvatar', 'Discarded final echo transcript', {
            transcript: trimmedTranscript,
          });
          return; // Discard — this is the avatar's own voice echoed back
        }

        logger.info('HeyGenAvatar', 'Voice transcript received', {
          transcript: trimmedTranscript,
        });

        // Interrupt avatar when we have a confirmed valid transcript
        if (avatarRef.current) {
          try {
            avatarRef.current.interrupt();
          } catch (e) {
            logger.warn(
              'HeyGenAvatar',
              'Failed to interrupt avatar on transcript',
              e instanceof Error ? e : new Error(String(e)),
            );
          }
          setIsAvatarTalking(false);
        }

        setSttFeedback(trimmedTranscript);
        handleUserMessageRef.current?.(trimmedTranscript);
      },
      onSpeechDetected: () => {
        lastActivityRef.current = Date.now();
        resetIdleTimer();
      },
      onError: (error: string) => {
        logger.error('HeyGenAvatar', 'Voice recognition error', new Error(error));
        setError(error);
      },
      onNoMatch: (reason?: 'empty' | 'low_confidence') => {
        if (reason === 'low_confidence') {
          setSttFeedback(t('avatar.sttLowConfidence', 'Low Confidence'));
        }
      },
      continuousMode: true,
      // Mic stays open during avatar speech so the user can interrupt at any time.
      // Echo detection above filters out the avatar's own audio.
      isMuted: isMicMuted || isProcessingMessage || sessionState !== SessionState.CONNECTED,
      language: sttLanguage,
    });

    // Hide interim transcript from UI if it evaluates as echo
    const displayInterimTranscript =
      interimTranscript && !isEchoTranscript(interimTranscript) ? interimTranscript : '';

    // ── Early Avatar Interruption ──
    // If the user starts speaking (real, non-echo words) while the avatar is talking,
    // instantly manually interrupt the avatar. This stops the avatar's audio, which
    // allows the browser STT to detect silence organically as soon as the user finishes,
    // guaranteeing a fast and reliable `isFinal` triggers to process the new question.
    useEffect(() => {
      if (displayInterimTranscript && isAvatarTalking) {
        const words = displayInterimTranscript.trim().split(/\s+/).filter(Boolean);
        // Prevent false positives from single tiny noises:
        const isSubstantialSpeech =
          words.length >= 2 || (words.length === 1 && words[0].length >= 3);

        if (isSubstantialSpeech) {
          logger.info('HeyGenAvatar', 'User interrupted via STT; silencing avatar early', {
            interim: displayInterimTranscript,
          });
          if (avatarRef.current) {
            try {
              avatarRef.current.interrupt();
            } catch (e) {
              logger.warn('HeyGenAvatar', 'Failed to interrupt avatar early', e);
            }
            setIsAvatarTalking(false);
          }
        }
      }
    }, [displayInterimTranscript, isAvatarTalking, setIsAvatarTalking, avatarRef]);

    // Detect if error is a microphone permission error
    const isMicError =
      error?.toLowerCase().includes('microphone access denied') ||
      error?.toLowerCase().includes('not-allowed');

    // Auto-dismiss error banner after 6 seconds
    useEffect(() => {
      if (error && !isMicError) {
        const timer = setTimeout(() => setError(null), 6000);
        return () => clearTimeout(timer);
      }
    }, [error, isMicError]);

    useEffect(() => {
      if (sessionState === SessionState.CONNECTED && avatarRef.current && mediaRef.current) {
        logger.info('HeyGenAvatar', 'Attaching avatar to video element');
        try {
          avatarRef.current.attach(mediaRef.current);

          // Try to play immediately if paused
          if (mediaRef.current.paused) {
            mediaRef.current
              .play()
              .catch((e) => logger.warn('HeyGenAvatar', 'Auto-play failed', e));
          }
        } catch (e) {
          logger.error(
            'HeyGenAvatar',
            'Failed to attach avatar',
            e instanceof Error ? e : new Error(String(e)),
          );
        }
      }
    }, [sessionState, avatarRef]);

    // Get user ID for queue system (using companionId as identifier)
    const userId = companionId;

    // Initial declaration to avoid circular dependency
    const startAvatarSessionRef = useRef<() => Promise<void>>(async () => {});

    // Helper to call the ref
    const onSessionStart = useCallback(async () => {
      await startAvatarSessionRef.current();
    }, []);

    const {
      isInQueue,
      isQueueReady,
      queuePosition,
      totalInQueue,
      queueCountdown,
      checkAndJoinQueue,
      leaveQueue,
      claimSlot,
    } = useHeyGenQueue(
      userId,
      onSessionStart,
      (err) => setError(err),
      onClose, // Called when queue is left (manual or expiration)
    );

    // Handle claiming queue slot
    const handleClaimSlot = useCallback(async () => {
      await claimSlot();
    }, [claimSlot]);

    const handleLeaveQueue = useCallback(async () => {
      await leaveQueue();
    }, [leaveQueue]);

    // Start avatar session (direct start, not from queue)
    const startAvatarSession = useCallback(async () => {
      setIsStarting(true);
      setError(null);
      setWarning(null);
      sessionTokenObtainedRef.current = false;
      // Notify parent that connecting has begun (hides chat overlay)
      onConnecting?.();

      try {
        const avatarId = getHeyGenAvatarId(companionId);
        logger.info('HeyGenAvatar', 'Starting session for avatar', { avatarId });

        // Get session token from backend (with retry logic)
        const tokenResponse = await getHeyGenSessionTokenWithRetry(avatarId);

        // Mark that backend session was created (for cleanup on cancel/tab close)
        sessionTokenObtainedRef.current = true;

        logger.info('HeyGenAvatar', 'Session token received', {
          maxDurationSeconds: tokenResponse.maxDurationSeconds,
          credits: tokenResponse.credits,
          expiresAt: tokenResponse.expiresAt,
        });

        // Store initial credit balance
        if (tokenResponse.credits) {
          setCreditsRemaining(tokenResponse.credits.available);
        }

        // Initialize avatar with token
        initAvatar(tokenResponse.token);

        // Start the avatar
        await startAvatar();

        logger.info('HeyGenAvatar', 'Avatar session started successfully');
        // Notify parent that avatar is ready (shows chat overlay)
        onConnected?.();
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to start avatar session';
        logger.error(
          'HeyGenAvatar',
          'Session start failed, falling back to idle video',
          err instanceof Error ? err : new Error(errorMessage),
        );
        // If token was obtained but SDK failed, clean up backend session
        if (sessionTokenObtainedRef.current) {
          endSession().catch(() => {});
          sessionTokenObtainedRef.current = false;
        }
        onError?.(err instanceof Error ? err : new Error(errorMessage));
        // Fall back to idle video instead of showing black screen
        onClose?.();
      } finally {
        setIsStarting(false);
      }
    }, [companionId, initAvatar, startAvatar, onError, onClose, onConnecting, onConnected]);

    // Update the ref so the hook calls the latest version
    useEffect(() => {
      startAvatarSessionRef.current = startAvatarSession;
    }, [startAvatarSession]);

    // Main entry point: check availability and either start or join queue
    const handleStartSession = useCallback(async () => {
      if (isStarting) return;
      setIsStarting(true);
      setError(null);
      setWarning(null);

      try {
        // Check queue first - fair queue logic enforced on backend
        const result = await checkAndJoinQueue();

        if (result.canStart) {
          // Can start directly - no queue needed
          await startAvatarSession();
        } else {
          // Added to queue - UI will update via hook state
          logger.info('HeyGenAvatar', 'Joined queue', {
            position: result.position,
            totalInQueue: result.totalInQueue,
          });
          setIsStarting(false);
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to start avatar session';
        setError(errorMessage);
        onError?.(err instanceof Error ? err : new Error(errorMessage));
        setIsStarting(false);
      }
    }, [startAvatarSession, checkAndJoinQueue, onError, isStarting]);

    /**
     * Make the avatar speak text using FULL Mode
     * Uses SDK's repeat() method which sends avatar.speak_text event via LiveKit data channel
     */
    const handleAvatarSpeak = useCallback(
      (text: string) => {
        if (!avatarRef.current) {
          logger.warn('HeyGenAvatar', 'Cannot speak: avatar session not initialized');
          return;
        }

        try {
          // Clean the text to remove user-perspective questions (Part 2 after the '|')
          const lines = text.split('\n');
          const numberedListPattern = /^(\d+[.)]|\*|-)\s+(.+)$/;
          const cleanedText = lines
            .map((line) => {
              const match = line.match(numberedListPattern);
              if (match) {
                const content = match[2];
                if (content.includes('|')) {
                  const parts = content.split('|');
                  // Return only Part 1 (Avatar perspective)
                  // We remove match[1] (the number/bullet) to make it sound more natural as a spoken offer
                  // This also fixes issues where Turkish numbers like "bir", "iki" sound robotic in lists
                  return parts[0].trim();
                }
              }
              return line;
            })
            .join('\n');

          // Set avatar talking state immediately for instant UI feedback
          setIsAvatarTalking(true);

          // Store the speech text for echo detection (so STT can filter
          // out the avatar's own voice being picked up by the mic)
          avatarSpeechTextRef.current = cleanedText;

          // Record timestamp so we can measure latency to actual AVATAR_SPEAK_STARTED
          speakCommandSentAtRef.current = Date.now();

          // Use SDK's repeat() method for FULL Mode
          try {
            avatarRef.current.repeat(cleanedText);
          } catch (err) {
            logger.warn('HeyGenAvatar', 'Speak command failed (sync)', err);
          }
          logger.info('HeyGenAvatar', 'Speak command sent', {
            originalLength: text.length,
            cleanedLength: cleanedText.length,
          });
        } catch (err) {
          logger.error(
            'HeyGenAvatar',
            'Failed to send speak command',
            err instanceof Error ? err : new Error(String(err)),
          );
          speakCommandSentAtRef.current = null;
          // Reset talking state on error
          setIsAvatarTalking(false);
        }
      },
      [avatarRef, setIsAvatarTalking],
    );

    // Log latency from speak command → avatar actually starts talking on stream
    useEffect(() => {
      if (isAvatarTalking && speakCommandSentAtRef.current !== null) {
        const latencySec = (Date.now() - speakCommandSentAtRef.current) / 1000;
        logger.info('HeyGenAvatar', 'Avatar speak latency (speak_text → stream start)', {
          avatarSpeakLatencySec: latencySec,
        });
        speakCommandSentAtRef.current = null;
      }
    }, [isAvatarTalking]);

    useImperativeHandle(ref, () => ({
      speak: handleAvatarSpeak,
    }));

    // Handle user message (from voice or text)
    const handleUserMessage = useCallback(
      async (message: string) => {
        if (!message.trim() || isProcessingMessage) return;

        const messageReceivedAt = Date.now();
        logger.info('HeyGenAvatar', 'handleUserMessage called', { messageLength: message.length });
        setIsProcessingMessage(true);
        setIsListening(false);

        try {
          // Add user message to history
          addMessage(MessageSender.CLIENT, message);

          // Send to RAG backend
          const ragStart = Date.now();
          const response: ChatResponse = await sendChatMessage(
            companionId,
            message,
            currentConversationId || undefined,
          );
          const ragCallDurationMs = Date.now() - ragStart;

          // Update conversation ID if new
          if (response.is_new_conversation) {
            setCurrentConversationId(response.conversation_id);
            onConversationCreated?.(response.conversation_id);
          }

          // Add avatar response to history
          addMessage(MessageSender.AVATAR, response.avatar_response);

          // Notify parent component
          onMessageSent?.(message, response.avatar_response);

          // Make avatar speak the RAG response
          if (avatarRef.current) {
            handleAvatarSpeak(response.avatar_response);
          }

          const totalToSpeakSec = (Date.now() - messageReceivedAt) / 1000;
          logger.info('HeyGenAvatar', 'Frontend pipeline timing', {
            ragCallDurationSec: ragCallDurationMs / 1000,
            totalToSpeakCommandSec: totalToSpeakSec,
          });
        } catch (err) {
          const errorMessage = err instanceof Error ? err.message : 'Failed to get response';
          const errLower = errorMessage.toLowerCase();
          logger.error(
            'HeyGenAvatar',
            'Error in handleUserMessage',
            err instanceof Error ? err : new Error(errorMessage),
          );

          // Detect credit/limit errors and show friendly modal instead of red banner
          if (
            errLower.includes('daily') ||
            errLower.includes('limit') ||
            errLower.includes('quota')
          ) {
            onCreditError?.('daily_limit');
          } else if (errLower.includes('insufficient') || errLower.includes('credit')) {
            onCreditError?.('no_credits');
          } else {
            // Network/timeout errors — show inline notification instead of red banner
            setNetworkError(errorMessage);
            networkErrorRetryRef.current = { message };
          }
        } finally {
          setIsProcessingMessage(false);
          setIsListening(true);
        }
      },
      [
        companionId,
        currentConversationId,
        isProcessingMessage,
        addMessage,
        onConversationCreated,
        onMessageSent,
        onCreditError,
        setIsListening,
        avatarRef,
        handleAvatarSpeak,
      ],
    );

    // Keep the ref updated with the latest handleUserMessage
    useEffect(() => {
      handleUserMessageRef.current = handleUserMessage;
    }, [handleUserMessage]);

    const isInactive = sessionState === SessionState.INACTIVE;
    const closingRef = useRef(false);

    // Keep-alive interval
    // Uses sessionStateRef.current (not the captured sessionState) to avoid a stale-closure
    // where the interval fires keepAlive() against an already-closed HeyGen session, which
    // produces the {"code":4000,"message":"Session already closed"} error.
    useEffect(() => {
      if (sessionState === SessionState.CONNECTED && avatarRef.current) {
        logger.info('HeyGenAvatar', 'Starting keep-alive interval');
        const keepAliveInterval = setInterval(() => {
          if (avatarRef.current && sessionStateRef.current === SessionState.CONNECTED) {
            logger.debug('HeyGenAvatar', 'Sending keep-alive ping');
            try {
              avatarRef.current.keepAlive().catch((err: unknown) => {
                const msg = err instanceof Error ? err.message : String(err);
                if (!msg.includes('Session already closed') && !msg.includes('not connected')) {
                  logger.warn('HeyGenAvatar', 'Keep-alive ping failed (async)', err);
                }
              });
            } catch (err: unknown) {
              const msg = err instanceof Error ? err.message : String(err);
              if (!msg.includes('Session already closed') && !msg.includes('not connected')) {
                logger.warn('HeyGenAvatar', 'Keep-alive ping failed (sync)', err);
              }
            }
          }
        }, 60000);

        return () => {
          logger.info('HeyGenAvatar', 'Stopping keep-alive interval');
          clearInterval(keepAliveInterval);
        };
      }
    }, [sessionState, avatarRef]);

    // Handle session close reason - show warning or error
    useEffect(() => {
      if (sessionCloseReason === 'credit_limit') {
        setWarning(
          'Session ended — you have used all your available credits. Purchase more credits to continue.',
        );
        setError(null);
        closingRef.current = true;
        setSessionCloseReason(null);
      } else if (sessionCloseReason === 'unexpected') {
        setError('Session disconnected unexpectedly. Please start a new session.');
        setWarning(null);
        closingRef.current = true;
        setSessionCloseReason(null);
      } else if (sessionCloseReason === 'user') {
        setWarning(null);
        setError(null);
        closingRef.current = true;
        setSessionCloseReason(null);
      }
    }, [sessionCloseReason, setSessionCloseReason]);

    // Auto-start avatar session when component mounts
    const hasStartedRef = useRef(false);

    useEffect(() => {
      if (hasStartedRef.current) return;

      if (isInactive && !isStarting && !error && !warning && !closingRef.current) {
        hasStartedRef.current = true;
        handleStartSession();
      }
    }, [isInactive, isStarting, error, warning, handleStartSession]);

    // Notify backend when session ends (CONNECTED → any other state)
    // This single effect handles ALL session end scenarios:
    // user click, idle timeout, unexpected disconnect, etc.
    const prevSessionStateRef = useRef(sessionState);
    useEffect(() => {
      const prev = prevSessionStateRef.current;
      prevSessionStateRef.current = sessionState;

      if (prev === SessionState.CONNECTED && sessionState !== SessionState.CONNECTED) {
        logger.info('HeyGenAvatar', 'Session ended, notifying backend for credit consumption');
        endSession().catch(() => {});
        // Reset credit state for next session
        setCreditsRemaining(null);
        setCreditLimitWarning(false);
      }
    }, [sessionState]);

    // Handle browser tab close — sendBeacon for both queue and active session
    useEffect(() => {
      const handleBeforeUnload = () => {
        if (isInQueue) {
          const apiUrl = (process.env.REACT_APP_API_URL || 'https://api.theychat.app').replace(
            /\/$/,
            '',
          );
          const url = `${apiUrl}/heygen/queue/leave`;
          const data = JSON.stringify({ userId });

          try {
            const idToken = localStorage.getItem('idToken');
            const headers: Record<string, string> = {
              'Content-Type': 'application/json',
            };
            if (idToken) {
              headers.Authorization = `Bearer ${idToken}`;
            }
            fetch(url, {
              method: 'POST',
              headers,
              body: data,
              keepalive: true,
            });
          } catch (_e) {
            leaveQueue().catch(() => {});
          }
        } else if (sessionState === SessionState.CONNECTED) {
          endSessionBeacon();
        } else if (isStarting && sessionTokenObtainedRef.current) {
          // Tab closed during startup after token was obtained — clean up backend session
          endSessionBeacon();
          sessionTokenObtainedRef.current = false;
        }
      };

      window.addEventListener('beforeunload', handleBeforeUnload);
      return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [userId, isInQueue, sessionState, isStarting, leaveQueue]);

    // Cleanup on SPA navigation (component unmount via React Router).
    // `beforeunload` only fires for browser tab close — it does NOT fire when
    // the user navigates away within the app (e.g. clicking the logo/navbar).
    // This effect fills that gap: on unmount, end any active or starting session.
    const sessionStateRef = useRef(sessionState);
    const isInQueueRef = useRef(isInQueue);
    const isStartingRef = useRef(isStarting);
    useEffect(() => {
      sessionStateRef.current = sessionState;
    }, [sessionState]);
    useEffect(() => {
      isInQueueRef.current = isInQueue;
    }, [isInQueue]);
    useEffect(() => {
      isStartingRef.current = isStarting;
    }, [isStarting]);

    useEffect(() => {
      const avatarInstance = avatarRef.current;

      return () => {
        // Use refs so the cleanup closure captures the latest values without stale closure issues
        if (isInQueueRef.current) {
          leaveQueueBeacon();
        } else if (
          sessionStateRef.current === SessionState.CONNECTED ||
          (isStartingRef.current && sessionTokenObtainedRef.current)
        ) {
          // Immediately notify SDK to cut connection (sync signal)
          try {
            avatarInstance?.stop();
          } catch (_e) {}

          // Fire-and-forget: endSession() or sendBeacon for best reliability on navigation
          endSessionBeacon();
        }
      };
    }, [avatarRef]);

    // Stop avatar session — accepts optional reason to pass through (e.g. 'credit_limit')
    const handleStopSession = useCallback(
      async (reason?: SessionCloseReason) => {
        closingRef.current = true;
        if (isInQueue) {
          await leaveQueue();
        }

        try {
          await stopAvatar(reason ?? undefined);
        } catch (_err) {
          // Ignore errors on stop
        } finally {
          if (onClose) {
            onClose();
          }
        }
        // Note: credit consumption is handled by the sessionState useEffect above
        // when sessionState transitions from CONNECTED → INACTIVE
      },
      [stopAvatar, onClose, isInQueue, leaveQueue],
    );

    const startIdleWarning = useCallback(() => {
      // Skip idle warning if the avatar is currently speaking — the session is active
      if (isAvatarTalkingRef.current) {
        logger.debug('HeyGenAvatar', 'Skipping idle warning — avatar is currently speaking');
        // Re-arm the idle timer so it fires again after the next idle period
        idleWarningTimerRef.current = setTimeout(() => {
          startIdleWarning();
        }, IDLE_WARNING_TIME);
        return;
      }

      const countdownStart = IDLE_CLOSE_TIME / 1000;
      setWarning(
        t('avatar.idleWarning', 'Are you still there? Closing session in {seconds}s...', {
          seconds: countdownStart,
        }),
      );

      // Set countdown interval
      let remaining = countdownStart;
      idleDisconnectTimerRef.current = setInterval(() => {
        remaining -= 1;
        if (remaining <= 0) {
          if (idleDisconnectTimerRef.current) {
            clearInterval(idleDisconnectTimerRef.current);
            idleDisconnectTimerRef.current = null;
          }
          logger.info('HeyGenAvatar', 'Idle timeout reached — closing session');
          setSessionCloseReason('idle_timeout');
          handleStopSession('idle_timeout');
        } else {
          setWarning(
            t('avatar.idleWarning', 'Are you still there? Closing session in {seconds}s...', {
              seconds: remaining,
            }),
          );
        }
      }, 1000);
    }, [t, handleStopSession, setSessionCloseReason]);

    // Inactivity / Idle detection logic (UI only)
    const resetIdleTimer = useCallback(() => {
      // Clear any existing warning/disconnect timers
      if (idleWarningTimerRef.current) {
        clearTimeout(idleWarningTimerRef.current);
        idleWarningTimerRef.current = null;
      }
      if (idleDisconnectTimerRef.current) {
        clearInterval(idleDisconnectTimerRef.current);
        idleDisconnectTimerRef.current = null;
      }

      setWarning(null);
      lastActivityRef.current = Date.now();

      if (sessionState !== SessionState.CONNECTED) return;

      // Set new warning timer
      idleWarningTimerRef.current = setTimeout(() => {
        startIdleWarning();
      }, IDLE_WARNING_TIME);
    }, [sessionState, startIdleWarning]);

    // Track user input for inactivity
    useEffect(() => {
      if (sessionState !== SessionState.CONNECTED) {
        if (idleWarningTimerRef.current) clearTimeout(idleWarningTimerRef.current);
        if (idleDisconnectTimerRef.current) clearInterval(idleDisconnectTimerRef.current);
        return;
      }

      const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'];
      const handler = () => {
        lastActivityRef.current = Date.now();
        resetIdleTimer();
      };

      for (const event of events) {
        window.addEventListener(event, handler);
      }

      // Initial start
      resetIdleTimer();

      return () => {
        for (const event of events) {
          window.removeEventListener(event, handler);
        }
        if (idleWarningTimerRef.current) clearTimeout(idleWarningTimerRef.current);
        if (idleDisconnectTimerRef.current) clearInterval(idleDisconnectTimerRef.current);
      };
    }, [sessionState, resetIdleTimer]);

    // Keep isAvatarTalkingRef in sync + reset idle timer when avatar starts/stops speaking
    useEffect(() => {
      isAvatarTalkingRef.current = isAvatarTalking;

      if (isAvatarTalking) {
        lastAvatarTalkedAtRef.current = Date.now();
      } else {
        // DO NOT clear echo text immediately when avatar stops talking!
        // Browser STT final transcripts are delayed. Keep the text around for 3 seconds
        // to successfully filter out trailing avatar echoes.
        const prevText = avatarSpeechTextRef.current;
        setTimeout(() => {
          if (!isAvatarTalkingRef.current && avatarSpeechTextRef.current === prevText) {
            avatarSpeechTextRef.current = '';
          }
        }, 3000);
      }

      if (sessionState === SessionState.CONNECTED) {
        // Avatar speaking/stopping is session activity — reset idle timer
        resetIdleTimer();
      }
    }, [isAvatarTalking, sessionState, resetIdleTimer]);

    // Heartbeat timer — deduct credits every 6s (0.2 credits per bucket) and auto-disconnect when exhausted
    useEffect(() => {
      // Clear warnings if we lose connection
      if (sessionState !== SessionState.CONNECTED) {
        setCreditLimitWarning(false);
        creditWarningDismissedRef.current = false;
        return;
      }

      heartbeatFailuresRef.current = 0;

      const heartbeatInterval = setInterval(async () => {
        const result = await sendSessionPing();

        if (!result) {
          heartbeatFailuresRef.current += 1;
          logger.warn('HeyGenAvatar', 'Heartbeat failed', {
            consecutiveFailures: heartbeatFailuresRef.current,
          });

          if (heartbeatFailuresRef.current >= MAX_HEARTBEAT_FAILURES) {
            logger.info('HeyGenAvatar', 'Max heartbeat failures reached, ending session');
            clearInterval(heartbeatInterval);
            setSessionCloseReason('credit_limit');
            handleStopSession('credit_limit');
          }
          return;
        }

        // Reset failure counter on success
        heartbeatFailuresRef.current = 0;

        logger.info('HeyGenAvatar', 'Heartbeat response', {
          billed: result.creditsBilled,
          totalBilled: result.totalBilledThisSession,
          remaining: result.remaining.total,
          shouldEnd: result.shouldEnd,
        });

        // Update the credits remaining state so the warning UI can render it
        setCreditsRemaining(result.remaining.total);

        if (result.shouldEnd) {
          logger.info('HeyGenAvatar', 'Credits exhausted, ending session');
          clearInterval(heartbeatInterval);
          setCreditLimitWarning(false);
          setSessionCloseReason('credit_limit');
          handleStopSession('credit_limit');
        } else if (result.remaining.total <= 2) {
          // Warn when 2 or fewer credits left (~60s remaining at 0.2 credits/6s)
          if (!creditWarningDismissedRef.current) {
            setCreditLimitWarning(true);
            logger.info('HeyGenAvatar', 'Credit limit warning', {
              remaining: result.remaining.total,
            });
          }
        } else {
          // Credits > 2, clear warning (handles top-ups)
          setCreditLimitWarning(false);
          creditWarningDismissedRef.current = false;
        }
      }, 6000); // Every 6 seconds — matches LiveAvatar's 6s billing bucket

      return () => {
        clearInterval(heartbeatInterval);
      };
    }, [sessionState, handleStopSession, setSessionCloseReason]);

    const handleResetIdleAndRestart = useCallback(() => {
      setWarning(null);
      closingRef.current = false;
      handleStartSession();
    }, [handleStartSession]);

    // Handle retry of a network-failed message
    const handleRetryNetworkError = useCallback(() => {
      const data = networkErrorRetryRef.current;
      if (!data) return;
      setNetworkError(null);
      networkErrorRetryRef.current = null;
      handleUserMessage(data.message);
    }, [handleUserMessage]);

    const handleDismissNetworkError = useCallback(() => {
      setNetworkError(null);
      networkErrorRetryRef.current = null;
    }, []);

    return (
      // biome-ignore lint/a11y/noStaticElementInteractions: Container tracks user movements to show/hide chrome overlay controls
      <div
        className={`heygen-avatar-container${sessionState === SessionState.CONNECTED && !chromeVisible ? ' chrome-hidden' : ''}`}
        onMouseMove={sessionState === SessionState.CONNECTED ? revealChrome : undefined}
        onTouchStart={sessionState === SessionState.CONNECTED ? revealChrome : undefined}
        onPointerDown={sessionState === SessionState.CONNECTED ? revealChrome : undefined}
      >
        {error && !isMicError && (
          <div className="heygen-avatar-error">
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              aria-hidden="true"
            >
              <title>Error Icon</title>
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            <span>{error}</span>
            <button type="button" onClick={() => setError(null)} aria-label="Dismiss error">
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                aria-hidden="true"
              >
                <title>Close</title>
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
        )}

        {error && isMicError && (
          <div className="heygen-mobile-overlay">
            <div className="heygen-mobile-modal">
              <div className="modal-icon error">
                <svg
                  width="48"
                  height="48"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  aria-hidden="true"
                >
                  <title>Microphone Error</title>
                  <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
                  <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                  <line x1="12" y1="19" x2="12" y2="23" />
                  <line x1="8" y1="23" x2="16" y2="23" />
                  <line x1="3" y1="3" x2="21" y2="21" />
                </svg>
              </div>
              <h4>{t('avatar.micAccessDenied', 'Microphone Access Denied')}</h4>
              <p>
                {t(
                  'avatar.micAccessErrorDetail',
                  'Recording is required for voice chat. Please enable microphone access in your browser settings and try again.',
                )}
              </p>
              <button type="button" onClick={() => setError(null)}>
                {t('common.ok', 'OK')}
              </button>
            </div>
          </div>
        )}

        {creditLimitWarning && (
          <div className="heygen-avatar-warning-overlay">
            <div className="heygen-avatar-warning-content">
              <p>
                {t(
                  'avatar.creditWarning',
                  'Your credits are running low ({{credits}} remaining). Session will end when credits are exhausted.',
                  {
                    credits: creditsRemaining ?? 0,
                  },
                )}
              </p>
              {onBuyCredits && (
                <button
                  type="button"
                  className="heygen-avatar-buy-credits-btn"
                  onClick={onBuyCredits}
                >
                  {t('avatar.buyMoreCredits', 'Buy More Credits')}
                </button>
              )}
              <button
                type="button"
                className="heygen-avatar-dismiss-btn"
                onClick={() => {
                  setCreditLimitWarning(false);
                  creditWarningDismissedRef.current = true;
                }}
              >
                {t('avatar.dismiss', 'Dismiss')}
              </button>
            </div>
          </div>
        )}

        {warning && (
          <div className="heygen-avatar-warning-overlay">
            <div className="heygen-avatar-warning-content">
              <p>{warning}</p>
              <button
                type="button"
                className="heygen-avatar-buy-credits-btn"
                onClick={handleResetIdleAndRestart}
              >
                {t('common.continue', 'Continue')}
              </button>
              <button
                type="button"
                className="heygen-avatar-dismiss-btn"
                onClick={() => handleStopSession('user')}
              >
                {t('common.close', 'Close')}
              </button>
            </div>
          </div>
        )}

        {networkError && (
          <div className="heygen-avatar-network-error">
            <div className="heygen-network-error-content">
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                aria-hidden="true"
              >
                <title>Network Error</title>
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
              <span>
                {t(
                  'avatar.networkError',
                  'Message could not be delivered. Please check your connection.',
                )}
              </span>
            </div>
            <div className="heygen-network-error-actions">
              <button
                type="button"
                className="heygen-network-retry-btn"
                onClick={handleRetryNetworkError}
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  aria-hidden="true"
                >
                  <title>Retry</title>
                  <polyline points="23 4 23 10 17 10" />
                  <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
                </svg>
                {t('avatar.retry', 'Retry')}
              </button>
              <button
                type="button"
                className="heygen-network-dismiss-btn"
                onClick={handleDismissNetworkError}
              >
                {t('avatar.dismiss', 'Dismiss')}
              </button>
            </div>
          </div>
        )}

        <QueueOverlay
          isInQueue={isInQueue}
          isQueueReady={isQueueReady}
          queuePosition={queuePosition}
          totalInQueue={totalInQueue}
          queueCountdown={queueCountdown}
          onClaimSlot={handleClaimSlot}
          onLeaveQueue={handleLeaveQueue}
        />

        {/* Real-time speech transcript overlay */}
        {(displayInterimTranscript || sttFeedback) && (
          <div className="heygen-avatar-stt-feedback">
            <span className={displayInterimTranscript ? 'stt-interim' : 'stt-final'}>
              {displayInterimTranscript || sttFeedback}
            </span>
          </div>
        )}

        <div className={`heygen-avatar-video-wrapper`}>
          <AvatarVideo
            ref={mediaRef}
            companionImage={companionImage}
            companionName={companionName}
            isStarting={isStarting}
            onCancelStart={() => {
              // Clean up backend session if token was already obtained
              if (sessionTokenObtainedRef.current) {
                endSession().catch(() => {});
                sessionTokenObtainedRef.current = false;
              }
              if (onCancelStart) {
                onCancelStart();
              } else if (onClose) {
                onClose();
              }
            }}
          />

          {isProcessingMessage && (
            <div className="heygen-avatar-thinking-indicator">
              <div className="thinking-dots">
                <span className="dot" />
                <span className="dot" />
                <span className="dot" />
              </div>
              <span className="thinking-text">{t('avatar.thinking', 'Thinking...')}</span>
            </div>
          )}

          {/* Hint bar — only visible when chrome is auto-hidden during a live session */}
          {sessionState === SessionState.CONNECTED && (
            <div className="heygen-avatar-hint-bar" aria-hidden="true">
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                aria-hidden="true"
              >
                <title>Touch</title>
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
              <span>{t('avatar.tapToShowControls', 'Tap to show controls')}</span>
            </div>
          )}

          {!isInactive && !isStarting && !isInQueue && (
            <div className="heygen-avatar-controls-overlay">
              <AvatarControls
                showChatToggle={true}
                onEndSession={handleStopSession}
                isDesktopChatVisible={showDesktopChat}
                onToggleDesktopChat={onToggleDesktopChat}
                isMobileChatVisible={showMobileChatOverlay}
                onToggleMobileChat={onToggleMobileChatOverlay}
                isMicMuted={isMicMuted}
                onToggleMute={() => setIsMicMuted(!isMicMuted)}
                isVoiceListening={isVoiceListening}
                isVoiceSupported={isVoiceSupported}
                isProcessing={isProcessingMessage}
              />
            </div>
          )}
        </div>
      </div>
    );
  },
);

// Main exported component with provider wrapper
export const HeyGenAvatar = forwardRef<HeyGenAvatarHandle, HeyGenAvatarProps>((props, ref) => {
  return (
    <StreamingAvatarProvider>
      <HeyGenAvatarInner {...props} ref={ref} />
    </StreamingAvatarProvider>
  );
});

export default HeyGenAvatar;
